<script setup lang="ts">
// shared
import { usePopupManager } from "@/shared/composables/usePopupManager";
import AlertReconfirmEvent from "@/shared/components/popup/AlertReconfirmEvent.vue";
import type { AlertReconfirmEventPopupProps } from "@/shared/plugins/interface";
import { useQueryErrorCapture } from "@/shared/composables/useQueryErrorCapture";

// entities
import useAuthenticationStore from "@/entities/user/infrastructure/userStore";

// features
import { useMutationApproveRegistrationUser } from "@/features/pending-registration/services/query/useMutationApproveRegistrationUser";
import { useMutationCancelRegistrationUser } from "@/features/pending-registration/services/query/useMutationCancelRegistartionUser";
import type { StudentDetailFormProps } from "@/features/pending-registration/models/interface";

// ui (임시...)
import { FormInput, BaseButton } from "@/shared/components/temparary/components";

const props = defineProps<StudentDetailFormProps>();

const { capture } = useQueryErrorCapture();
const { openPopup, clearPopup } = usePopupManager();
const authenticationStore = useAuthenticationStore();

const { approveRegistrationUser, isPending: isApprovePending } = useMutationApproveRegistrationUser(
    {
        id: props.registrationId,
        academyId: authenticationStore.authState.academyId,
        userId: props.convertedFormData.id,
        onSuccessCallback: () => {
            clearPopup();
        },
        onErrorCallback: (error) => {
            capture(error);
        },
    },
);

const { cancelRegistrationUser, isPending: isCancelPending } = useMutationCancelRegistrationUser({
    id: props.registrationId,
    academyId: authenticationStore.authState.academyId,
    userId: props.convertedFormData.id,
    onSuccessCallback: () => {
        clearPopup();
    },
    onErrorCallback: (error) => {
        capture(error);
    },
});

const clickRejectButton = () => {
    openPopup<AlertReconfirmEventPopupProps>({
        component: AlertReconfirmEvent,
        props: {
            closeKey: "",
            rest: {
                title: "승인을 거절하시면 다시 신청하시기 전까지 승인할 수 없습니다.\n거절하시겠습니까?",
                subTitle: "확인",
                confirmCallback: () => {
                    cancelRegistrationUser();
                },
            },
        },
    });
};

const clickApproveButton = () => {
    approveRegistrationUser();
};
</script>

<template>
    <div class="sm:w-[51.2rem] w-full h-auto mt-[2.4rem]">
        <div
            class="size-full flex flex-col justify-start items-start gap-[1.2rem] py-[1rem] max-h-[40rem] sm:max-h-[55rem] overflow-y-auto"
        >
            <FormInput
                :label="'타입'"
                :name="'type'"
                :model-value="convertedFormData.type"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <FormInput
                :label="'아이디'"
                :name="'email'"
                :model-value="convertedFormData.email"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <FormInput
                :label="'이름'"
                :name="'name'"
                :model-value="convertedFormData.name"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <div class="w-full flex md:flex-row flex-col justify-start items-center gap-[0.8rem]">
                <FormInput
                    :label="'학생 휴대폰 번호'"
                    :name="'phone'"
                    :model-value="convertedFormData.phone.countryCode"
                    :disabled="true"
                    :type="'text'"
                    :value="convertedFormData"
                    :validation="[]"
                />
                <FormInput
                    :name="'phone'"
                    :model-value="convertedFormData.phone.subscriberNumber"
                    :disabled="true"
                    :type="'text'"
                    :value="convertedFormData"
                    :validation="[]"
                    :is-not-label="true"
                />
            </div>
            <FormInput
                :name="'birthday'"
                :label="'생년월일'"
                :model-value="convertedFormData.birthday"
                :value="convertedFormData"
                :disabled="true"
                :type="'text'"
                :validation="[]"
            />
            <FormInput
                :name="'location'"
                :label="'지역'"
                :model-value="convertedFormData.location"
                :value="convertedFormData"
                :disabled="true"
                :type="'text'"
                :validation="[]"
            />
            <FormInput
                :label="'학교명'"
                :name="'school'"
                :model-value="convertedFormData.school"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <FormInput
                :label="'학년'"
                :name="'grade'"
                :model-value="convertedFormData.grade"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <FormInput
                :label="'학부모명'"
                :name="'parentName'"
                :model-value="convertedFormData.parentName"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <FormInput
                :label="'학부모 휴대전화번호'"
                :name="'parentPhone'"
                :model-value="convertedFormData.parentPhone"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <FormInput
                :label="'주소'"
                :name="'address1'"
                :model-value="convertedFormData.address1"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
            <FormInput
                :label="'상세 주소'"
                :name="'address2'"
                :model-value="convertedFormData.address2"
                :disabled="true"
                :type="'text'"
                :value="convertedFormData"
                :validation="[]"
            />
        </div>
        <div class="w-full flex justify-end items-center mt-[2.4rem] gap-[1.2rem]">
            <BaseButton
                :type="'button'"
                :color="'normal'"
                :value="'거절'"
                :responsive-mobile-width="true"
                :is-loading="isCancelPending"
                @click="clickRejectButton"
            />
            <BaseButton
                :type="'button'"
                :color="'primary'"
                :value="'승인'"
                :responsive-mobile-width="true"
                :is-loading="isApprovePending"
                @click="clickApproveButton"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped></style>
